import styled from '@emotion/styled'
import * as React from 'react'

import { BgColorProvider } from '~theme/BgColorProvider'
import { TActiveColorKey, TInputColorKey } from '~theme/typings'
import { getActiveColorFullPath, getColorValue } from '~theme/utils'
import { TButtonVariantProps } from '~ui/components/buttons/Button/buildButtonVariantProps'
import { Button } from '~ui/components/buttons/Button/Button'
import { Dropin } from '~ui/components/content/Dropin/Dropin'
import { Flex } from '~ui/components/layout/Box'
import { getNestedRadiusValue, TSingleRadius } from '~ui/styleGuide/radiuses'
import { useTheme } from '~ui/theme/ThemeProvider'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

type TCommonButtonProps = TButtonVariantProps &
  Pick<PropsOf<typeof Button>, 'radius' | 'shadow' | 'minWidth' | 'padding'>

type TStyledProps = {
  /**
   * radius of the MultiSwitch container
   */
  radius: TSingleRadius
  /**
   * total count of all possible options
   * used to position the selected element correctly
   */
  optionCount: number
  /**
   * bgColor of the element marking the selected color
   */
  selectedColor: string
  /**
   * index of the selected option in the option array
   * used to position the selected element correctly
   */
  selectedOptionIndex: number
}

type TProps<TOption> = NoChildren &
  TestingProps & {
    /**
     * variant of the MultiSwitch
     */
    variant?: `buttons` | `switch`
    /**
     * radius of the MultiSwitch container
     */
    radius?: TSingleRadius
    /**
     * bg color of the whole MultiSwitch
     */
    bgColor?: TInputColorKey
    /**
     * array of options for which the MultiSwitch should be rendered
     * use with 2 - ~5 options
     */
    options: RoA<TOption>
    /**
     * prop of the option that will return string/number id, unique amongst the array
     * used as a key AND for comparing with selected option
     */
    idProp: keyof TOption
    /**
     * color of the element highlighting the selected option
     */
    selectedColor?: TActiveColorKey
    /**
     * option from the options array, that is currently selected
     */
    selectedOption: TOption
    /**
     * disables the whole switch and changes the color
     */
    isDisabled?: boolean
    /**
     * callback on option click
     */
    onSelect: (option: TOption) => void
    /**
     * option renderer
     */
    renderLabel: (option: TOption, isSelected: boolean) => React.ReactNode
  }

// get rid of the React error when passing unknown prop to the HTML element
const propForwardOptions = {
  shouldForwardProp: (propName: string) =>
    propName !== `optionCount` &&
    propName !== `selectedColor` &&
    propName !== `selectedOptionIndex`,
}

const StyledMultiSwitch = styled(Dropin, propForwardOptions)<TStyledProps>`
  --padding: 2px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  align-items: center;

  position: relative;

  /* horizontal padding is set to center button labels correctly */
  padding: 0 var(--padding);

  /* selected element highlight (moving bg) */
  &:before {
    /* space inside the MultiSwitch, around the selected element highlight */
    --option-width: ${({ optionCount }) => `calc((100% - 2 * var(--padding)) / ${optionCount})`};

    content: '';
    width: var(--option-width);

    position: absolute;
    top: var(--padding);
    bottom: var(--padding);
    /* move the bg based on which option is selected */
    left: ${({ selectedOptionIndex }) =>
      `calc(var(--padding) + ${selectedOptionIndex} * var(--option-width))`};

    transition: left 0.2s;

    background: ${({ selectedColor }) => selectedColor};
    border-radius: ${({ radius }) => {
      return getNestedRadiusValue({ radius, paddingValue: `var(--padding)` })
    }};
  }
`

export const MultiSwitch = <TOption,>({
  idProp,
  radius = `medium`,
  variant = `switch`,
  options,
  onSelect,
  renderLabel,
  selectedOption,
  bgColor = `inputOnPrimary`,
  isDisabled = false,
  selectedColor = `primary`,
  dataTestId,
}: TProps<TOption>) => {
  const theme = useTheme()

  const bgColorKey = getActiveColorFullPath({ bgColor })
  const selectedColorKey = getActiveColorFullPath({ bgColor: selectedColor, isDisabled })
  const selectedColorValue = getColorValue(theme)(selectedColorKey)

  // index of the selected option in the options array, of 0 if not found
  const selectedOptionIndex = Math.max(
    options.findIndex((option) => option[idProp] === selectedOption[idProp]),
    0,
  )

  const isSwitch = variant === 'switch'

  const renderContent = () => {
    return options.map((option) => {
      const isSelected = Boolean(option[idProp] === selectedOption[idProp])
      const visibleBgKey = isSelected ? selectedColorKey : bgColorKey
      const visibleBgColor = isSelected ? selectedColor : bgColor

      const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: visibleBgKey }

      // because the visible bg here is implemented
      // by moving absolutely positioned (pseudo)element behind options
      // such bg wouldn’t be selectable in theme design tools
      // apart from not solving it, options were to pass the bg key to Button as data- attribute override
      // or add an element with that attribute here
      // from those, the latter was chosen because so far this is a single place with such logic
      // and solving it here was better then bloating Button props (plain div is used instead of Box for similar reason)

      const buttonsVariantProps: TCommonButtonProps = {
        shadow: `light`,
        variant: `solid`,
        radius: `rounded`,
        bgColor: visibleBgColor,
        padding: { vertical: `smallMedium`, horizontal: `large` },
      } as const

      const defaultProps: TCommonButtonProps = {
        shadow: `none`,
        variant: `ghost`,
        padding: { vertical: `smallMedium`, horizontal: `none` },
      } as const

      const buttonProps = isSwitch ? defaultProps : buttonsVariantProps

      return (
        <div {...themeDesignProps} key={String(option[idProp])}>
          <Button {...buttonProps} width={`100%`} onClick={() => onSelect(option)}>
            <BgColorProvider bgColor={visibleBgKey}>
              {renderLabel(option, isSelected)}
            </BgColorProvider>
          </Button>
        </div>
      )
    })
  }

  if (!isSwitch) {
    return (
      <Flex flexWrap={`wrap`} gap={`small`}>
        {renderContent()}
      </Flex>
    )
  }

  return (
    <StyledMultiSwitch
      radius={radius}
      padding={`none`}
      bgColor={bgColorKey}
      shadowInset={`lightSharp`}
      optionCount={options.length}
      selectedColor={selectedColorValue}
      selectedOptionIndex={selectedOptionIndex}
      dataTestId={dataTestId}
    >
      {renderContent()}
    </StyledMultiSwitch>
  )
}
